import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

import { Card } from "@hr/common/custom/Card";
import { Button } from "@hr/common/Button";
import { buildAlert } from "@hr/actions/index.js";
import { ManualTabContext } from "@hr/statisticsview/vacancy/ManualTab/ManualTabContext";
import { getGetCompanyBalancesQueryKey, useBatchUpdateBalances } from "@hr/api/endpoints";
import { ManualTabGridRef } from "@hr/statisticsview/vacancy/ManualTab/ManualTab.types";
import { capitalizeFirstLetter, lowerCaseFirstLetter } from "../../../common/utils.jsx";

export const ManualTabControls: FC<{ gridRef: ManualTabGridRef }> = ({ gridRef }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [operation, setOperation] = useState("set");
  const [value, setValue] = useState(1);
  const [column, setColumn] = useState("vacationAllowanceDays");

  const { mutate, isSuccess } = useBatchUpdateBalances();
  const { selectedRows } = useContext(ManualTabContext);

  const handleSetForSelected = () => {
    const rowsToUpdate = selectedRows.map((row) => ({
      ...row,
      [column]: operation === "set" ? value : (Number(row[column]) || 0) + value, // set value or add to existing value
    }));
    mutate({ data: rowsToUpdate });
  };

  useEffect(() => {
    if (isSuccess) {
      queryClient.invalidateQueries({ queryKey: getGetCompanyBalancesQueryKey() });
      gridRef?.current?.api.deselectAll();
      dispatch(
        buildAlert(
          "SUCCESS",
          t("ImportStatus_SUCCESS", "Onnistui"),
          t("ItemsUpdatedSuccessfully", "Kohteiden päivitys onnistui."),
        ),
      );
    }
  }, [isSuccess]);

  return (
    <div className="d-flex justify-content-between mb-3">
      <Card body bg="primary" color="primary" style={{ width: "40rem" }}>
        <div className="d-flex align-items-center justify-content-between gap-2">
          <div>
            <div className="fw-bold">{t("ActionsForSelectedRows", "Toiminnot valituille riveille")}</div>
              <div className="d-flex align-items-center gap-1">
              <select onChange={(e) => setOperation(e.target.value)}>
                <option value="set">{t("Set", "Aseta")}</option>
                <option value="add">{t("Add", "Lisää")}</option>
              </select>
              {lowerCaseFirstLetter(t("Value", "Arvo"))}
                <input
                type="number"
                value={value}
                onChange={(event) => setValue(event.target.valueAsNumber)}
                style={{ width: "3rem", textAlign: "center" }}
              />
              {lowerCaseFirstLetter(t("ToColumn", "Sarakkeeseen"))}
              <select onChange={(e) => setColumn(e.target.value)}>
                  <option value="vacationAllowanceDays">{t("EarnedVacationDays", "Ansaitut lomapäivät")}</option>
                  <option value="vacationExtraDays">{t("ExtraDays", "Lisäpäivät")}</option>
                  <option value="usedVacationDays">{t("UsedVacationDays", "Käytetyt lomapäivät")}</option>
                  <option value="saturdaysTotal">{t("Saturdays", "Lauantait")}</option>
                  <option value="saturdaysUsed">{t("UsedSaturdays", "Käytetyt lauantait")}</option>
              </select>
            </div>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={handleSetForSelected} className="mt-3 h-auto" disabled={!selectedRows.length}>
            <strong>
              {`${t("PerformActionForSelectedPersons", "Suorita toiminto valituille henkilöille")} (${selectedRows.length})`}
            </strong>
          </Button>
        </div>
      </Card>
      {/*No endpoint yet. Disabling for now.*/}
      {/*<div className="d-flex align-items-end gap-2">*/}
      {/*  <Button variant="outline-secondary" size="sm" className="component-in-progress">*/}
      {/*    <FilterCrossedIcon />*/}
      {/*    {t("Update selected from Payroll")}*/}
      {/*  </Button>*/}
      {/*  <Button variant="outline-secondary" size="sm" className="component-in-progress">*/}
      {/*    <ReturnIcon />*/}
      {/*    {t("Cancel changes")}*/}
      {/*  </Button>*/}
      {/*</div>*/}
    </div>
  );
};
